import { useTranslation } from 'react-i18next';
import type { ViewFetchState } from '@bliks-io/ui';
import type { ProcessStepAssignment } from '@/api/queries/useTasks';
import type {
  AssignmentProcess,
  AssignmentUser,
  GetSurveyResponse,
  SubmitSurveyRequest,
  SubmitVariantResponseRequest
} from '@bliks-io/contracts';
import { AssignmentReviewModal } from './AssignmentReviewModal';
import { ReassignContextDialog } from '@/components/processes/ReassignContextDialog';
import { ReviewAlert } from '@/components/processes/ReviewAlert';
import { ReviewUserSection } from '@/components/processes/ReviewUserSection';
import { ReviewProcessDetails } from '@/components/processes/ReviewProcessDetails';
import { StepPreviewWithProcessMap } from '@/components/processes/StepPreviewWithProcessMap';
import { isStartAnchoredSource } from '@/lib/assignment-source';
import { SurveyDialog, type VariantAnswerOutcome } from '@/components/processes/SurveyDialog';
import type { SuccessorUpdateData } from '@/pages/processes/recording/types';
import { toReassignPayload, type ReassignPayload } from '@/lib/successorEntityMapping';
import { getProcessId } from '../utils/assignmentHelpers';

interface TaskModalsProps {
  selectedAssignment: ProcessStepAssignment | null;
  onAssignmentClose: () => void;
  selectedReassignment: ProcessStepAssignment | null;
  onReassignmentClose: () => void;
  onReassignSubmit: (data: ReassignPayload) => void;
  isReassignmentSubmitting: boolean;
  selectedSurveyId: string | null;
  /** The fetched survey and the server's toggles, together (ADR 0022). */
  surveyResponse: GetSurveyResponse | undefined;
  surveyLoadState: ViewFetchState;
  onSurveyClose: () => void;
  onSurveySubmit: (response: SubmitSurveyRequest) => void;
  onVariantAnswer: (
    answer: SubmitVariantResponseRequest,
    outcome: VariantAnswerOutcome
  ) => Promise<void> | void;
  isSurveySubmitting: boolean;
}

export function TaskModals({
  selectedAssignment,
  onAssignmentClose,
  selectedReassignment,
  onReassignmentClose,
  onReassignSubmit,
  isReassignmentSubmitting,
  selectedSurveyId,
  surveyResponse,
  surveyLoadState,
  onSurveyClose,
  onSurveySubmit,
  onVariantAnswer,
  isSurveySubmitting
}: TaskModalsProps) {
  const { t } = useTranslation();
  const reassignmentProcessId = getProcessId(selectedReassignment?.process);
  const reassignmentRecordingId = selectedReassignment?.processStepRecording;
  const isResolvingUnclear = selectedReassignment?.status === 'pending-unclear';

  const handleSave = (data: SuccessorUpdateData) => {
    const payload = toReassignPayload(data);
    if (payload) onReassignSubmit(payload);
  };

  return (
    <>
      {/* Assignment Review Modal */}
      <AssignmentReviewModal
        open={!!selectedAssignment}
        onOpenChange={(open) => !open && onAssignmentClose()}
        assignment={selectedAssignment}
      />

      {/* Reassign / Resolve-unclear flow — two-step: context, then picker. */}
      <ReassignContextDialog
        open={!!selectedReassignment}
        onOpenChange={(open) => !open && onReassignmentClose()}
        onSubmit={handleSave}
        title={t('reassignContext.title')}
        description={
          isResolvingUnclear
            ? t('reassignContext.descriptionPendingUnclear')
            : t('reassignContext.descriptionPending')
        }
        contextBody={
          selectedReassignment ? (
            <PendingUnclearContext
              assignment={selectedReassignment}
              showUnclearAlert={isResolvingUnclear}
            />
          ) : null
        }
        processId={reassignmentProcessId}
        recordingId={reassignmentRecordingId}
        hideUnclear={isResolvingUnclear}
        hideExternalContact={isStartAnchoredSource(selectedReassignment?.sourceProcessStep)}
        isSubmitting={isReassignmentSubmitting}
      />

      {/* Survey Dialog */}
      <SurveyDialog
        key={selectedSurveyId || 'survey-dialog'}
        open={!!selectedSurveyId}
        onOpenChange={(open) => !open && onSurveyClose()}
        onSubmit={onSurveySubmit}
        onVariantAnswer={onVariantAnswer}
        isSubmitting={isSurveySubmitting}
        surveyResponse={surveyResponse}
        loadState={surveyLoadState}
      />
    </>
  );
}

interface PendingUnclearContextProps {
  assignment: ProcessStepAssignment;
  showUnclearAlert: boolean;
}

function PendingUnclearContext({ assignment, showUnclearAlert }: PendingUnclearContextProps) {
  const { t } = useTranslation();
  const populatedProcess =
    typeof assignment.process === 'object' ? (assignment.process as AssignmentProcess) : null;
  const assignedBy =
    typeof assignment.assignedBy === 'object' && assignment.assignedBy !== null
      ? (assignment.assignedBy as AssignmentUser)
      : null;

  return (
    <div className="space-y-6">
      {showUnclearAlert && (
        <ReviewAlert
          variant="info"
          title={t('reassignContext.unclearAlertTitle')}
          description={t('reassignContext.unclearAlertDescription')}
        />
      )}

      {(assignedBy || populatedProcess) && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 md:items-stretch">
          {assignedBy && (
            <div className="md:col-span-2">
              <ReviewUserSection label={t('reassignContext.markedUnclearBy')} user={assignedBy} />
            </div>
          )}
          {populatedProcess && (
            <div className={assignedBy ? 'md:col-span-3' : 'md:col-span-5 col-span-full'}>
              <ReviewProcessDetails process={populatedProcess} />
            </div>
          )}
        </div>
      )}

      {assignment.sourceProcessStep && (
        <StepPreviewWithProcessMap
          label={t('reassignContext.stepDetails')}
          sourceProcessStep={assignment.sourceProcessStep}
          showUnclearSuccessorWarning={showUnclearAlert}
          ruleIds={assignment.ruleIds}
          // This body only exists while the context dialog is open, so the
          // fetch is already gated by its own mount. Its key is the one
          // `ReassignContextDialog` opens for the same process — one query.
          hostOpen
          processId={getProcessId(assignment.process)}
          // A pending-unclear row already has a recording (the recorder marked
          // their own step unclear); a plain pending row does not, and points
          // at the published node instead.
          highlightNodeId={
            assignment.processStepRecording || assignment.stableStepId || assignment._id
          }
        />
      )}
    </div>
  );
}
